/**
 * Toucan People — bringing a guest list into the app.
 *
 * One source, in two shapes: a list of bare names, and an event platform's
 * members export. A file with a header row it recognises brings columns with
 * it — an email address, the id a public profile is addressed by, and how many
 * events each person was approved for — and every one of those is what makes
 * the import worth doing by hand rather than in bulk.
 *
 * A guest list is mostly people who came once, so the threshold is the first
 * thing the screen offers: the people worth learning are the ones who come
 * back. Then one pass looks for a face — the profile's own avatar, or a
 * Gravatar for the address it was booked with — and everything else is a
 * link to open and a picture to paste. Nothing is added until every row has
 * been looked at, because a photograph of the wrong person is worse than none.
 *
 * This file is the reading and the deciding; the screen that shows it is
 * `pages/ImportPage.tsx`.
 */

import {
  comparableWords,
  fetchPage,
  sha256Hex,
  storePhotos,
  timeLeft,
} from "toucan";
import { addPerson } from "./model.ts";
import type { PeopleStore, PersonInput } from "./model.ts";

/** One person a paste offered, and whatever the file said about them. */
export interface ImportLine {
  name: string;
  email?: string;
  /** The id their public profile is addressed by, where the file gave one. */
  profileId?: string;
  /** How many events they were approved for, where the file counted. */
  events?: number;
}

/** What a paste came to. */
export interface Paste {
  lines: ImportLine[];
  /**
   * The most events anyone in the list came to, which is what the threshold
   * runs up to. Zero where nothing in the file counted, and the whole of what
   * the screen needs to know to leave the threshold out.
   */
  most: number;
}

/**
 * The columns worth reading out of a members export. Everything else in one is
 * empty, about money, or a count of QR check-ins — which reads like the useful
 * column and is not: the scanner only ran at some events.
 */
const COLUMNS = {
  name: "name",
  email: "email",
  profileId: "user_id",
  events: "event_approved_count",
};

/**
 * Split CSV text into its rows. Quoted fields hold commas, newlines and
 * doubled quotes standing for one, which is more than a `split` can carry —
 * and a members export is written by a machine, so it uses all three.
 */
function csvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  // A field is only finished by what follows it, so the last one is pushed
  // after the walk rather than inside it.
  // `charAt` rather than an index, which reads as possibly missing inside the
  // very loop that has just checked the length.
  for (let at = 0; at < text.length; at += 1) {
    const char = text.charAt(at);
    if (quoted) {
      if (char !== '"') {
        field += char;
      } else if (text.charAt(at + 1) === '"') {
        field += '"';
        at += 1;
      } else {
        quoted = false;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }
  row.push(field.replace(/\r$/, ""));
  rows.push(row);
  return rows.filter((entry) => entry.some((value) => value.trim() !== ""));
}

/** A byte-order mark, which a spreadsheet writes onto the first header. */
function withoutMark(text: string): string {
  return text.replace(/^\uFEFF/, "");
}

/** Which column each field was written in, for the headers a file has. */
function columnsOf(
  header: string[],
): Partial<Record<keyof ImportLine, number>> {
  const at: Partial<Record<keyof ImportLine, number>> = {};
  for (const [field, heading] of Object.entries(COLUMNS)) {
    const index = header.indexOf(heading);
    if (index !== -1) {
      at[field as keyof ImportLine] = index;
    }
  }
  return at;
}

/** One row's value for a column, or "" where the file has no such column. */
function cell(row: string[], index: number | undefined): string {
  return index === undefined ? "" : (row[index] ?? "").trim();
}

/** The line one row of a members export describes, or null for a nameless one. */
function csvLine(
  row: string[],
  at: Partial<Record<keyof ImportLine, number>>,
): ImportLine | null {
  const name = cell(row, at.name);
  if (name === "") {
    return null;
  }
  const line: ImportLine = { name };
  const email = cell(row, at.email);
  if (email !== "") {
    line.email = email;
  }
  const profileId = cell(row, at.profileId);
  if (profileId !== "") {
    line.profileId = profileId;
  }
  const events = Number(cell(row, at.events));
  if (Number.isFinite(events) && events > 0) {
    line.events = events;
  }
  return line;
}

/**
 * Parse a paste: a members export where the first row names its columns, and
 * one name per line where it does not. A leading list marker ("- ", "1. ") is
 * dropped, so a list pasted out of notes doesn't need tidying first.
 */
export function parsePaste(text: string): Paste {
  const rows = csvRows(withoutMark(text));
  const header = (rows[0] ?? []).map((value) => value.trim().toLowerCase());
  const at = columnsOf(header);
  const lines: ImportLine[] =
    at.name === undefined
      ? text
          .split("\n")
          .map((raw) =>
            withoutMark(raw)
              .trim()
              .replace(/^(?:[-*•]|\d+[.)])\s+/, ""),
          )
          .filter((name) => name !== "")
          .map((name) => ({ name }))
      : rows.slice(1).flatMap((row) => {
          const line = csvLine(row, at);
          return line === null ? [] : [line];
        });
  return {
    lines,
    most: lines.reduce((most, line) => Math.max(most, line.events ?? 0), 0),
  };
}

/** The people who came to at least `events` of them, so 0 keeps everyone. */
export function atLeast(lines: ImportLine[], events: number): ImportLine[] {
  return lines.filter((line) => (line.events ?? 0) >= events);
}

/** One person in the review, and everything the screen knows about them. */
export interface ImportRow {
  line: ImportLine;
  /** The photos they will be added with, as blob hashes. */
  photos: string[];
  /**
   * What is known about them, typed on the row. Looking somebody up turns up
   * who they are at the same moment as their photograph — the profile being
   * read for a face says what they do underneath it — so the note is written
   * where that is on screen rather than in an edit afterwards.
   */
  note: string;
  /** Whether they will be added when the import is saved. */
  chosen: boolean;
  /**
   * Why the row arrived switched off, or "" for one that is on. Somebody
   * already in the app, or named twice in the same paste — the source repeats
   * itself, so the second copy is as much a duplicate as the first.
   */
  duplicate: string;
  /** The LinkedIn profile the lookup found, or "" for none. */
  linkedin: string;
  /** What the lookup came to, in the row's own words, or "" before it runs. */
  found: string;
}

/**
 * A name as two spellings of it meet: case, accents and punctuation are all
 * ways one person's name gets written differently by two sources.
 */
function sameName(name: string): string {
  return comparableWords(name).join(" ");
}

/**
 * The review's rows, with everyone this app looks like it already has switched
 * off. Matching on the name alone is enough, because a guest list has a few
 * hundred people, and two of them sharing a name is rarer than the same
 * person being imported twice.
 */
export function rowsFor(store: PeopleStore, lines: ImportLine[]): ImportRow[] {
  const known = new Set(store.people.map((person) => sameName(person.name)));
  const seen = new Set<string>();
  return lines.map((line) => {
    const name = sameName(line.name);
    const duplicate = known.has(name)
      ? "already in People"
      : seen.has(name)
        ? "listed twice in this paste"
        : "";
    seen.add(name);
    return {
      line,
      photos: [],
      note: "",
      chosen: duplicate === "",
      duplicate,
      linkedin: "",
      found: "",
    };
  });
}

// ---------------------------------------------------------------- looking

/** The URL of a public profile, for the id a members export gives. */
function profileUrl(profileId: string): string {
  return `https://luma.com/user/${profileId}`;
}

/** What one profile page gave up. */
export interface Profile {
  /**
   * The avatar's path on the image host, or "" where the page shows a
   * generated placeholder — which is a picture of nobody.
   */
  avatar: string;
  /** The LinkedIn profile it links to, or "" where it links to none. */
  linkedin: string;
}

/**
 * Read a profile page. This is page scraping rather than an API: the platform's
 * own guest endpoint returns neither the avatar nor the social links, so there
 * is no supported route to the same two facts. It will break when the markup
 * changes, and a break has to read as "no photo found" rather than as an error
 * — which is what returning empty strings is for.
 */
export function readProfile(html: string): Profile {
  // Matches only the path a real photograph is under. A page with no avatar
  // is a person with no picture, and so is a page showing a placeholder under
  // `avatars-default/` generated from the person's initials.
  const avatar = /"avatar_url":"https:\/\/[^"/]+\/(avatars\/[^"]+)"/.exec(html);
  const linkedin = /https?:\/\/(?:[\w-]+\.)?linkedin\.com\/in\/[\w%-]+/.exec(
    html,
  );
  return { avatar: avatar?.[1] ?? "", linkedin: linkedin?.[0] ?? "" };
}

/**
 * Where to ask the image host for an avatar at the size this app keeps.
 *
 * The segment after `/cdn-cgi/image/` is a transform the host applies on the
 * way out, and every parameter in it can be asked for — so the picture arrives
 * at {@link MAX_PHOTO_EDGE} rather than at the 112px the profile page draws,
 * and a 3 MB original never crosses the wire. `scale-down` keeps the shape a
 * portrait was taken in, where `cover` would crop it square before this app
 * ever saw it.
 */
export function avatarUrl(path: string): string {
  return `https://images.lumacdn.com/cdn-cgi/image/format=jpeg,fit=scale-down,quality=85,width=1200,height=1200/${path}`;
}

/**
 * Where a person's Gravatar is, for the address they booked with. `d=404` is
 * what makes the answer usable: without it the service draws a pattern for an
 * address it has never heard of, and a pattern is not a face.
 */
export async function gravatarUrl(email: string): Promise<string> {
  const hash = await sha256Hex(
    new TextEncoder().encode(email.trim().toLowerCase()),
  );
  return `https://gravatar.com/avatar/${hash}?d=404&s=1200`;
}

/**
 * Store a picture from an address the browser may read itself. Both hosts send
 * `access-control-allow-origin: *`, so these bytes never go through the
 * server — and a refusal (a 404 from Gravatar, a host that will not answer)
 * is an answer meaning "no photo", never a reason to stop the run.
 */
async function keepPhoto(url: string): Promise<string[]> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return [];
    }
    const stored = await storePhotos([await response.blob()]);
    // A picture that arrived and could not be kept looks the same on the row
    // as one that never arrived, so it says why here.
    if (stored.hashes.length === 0) {
      console.warn(`photo not kept: ${url}`, stored);
    }
    return stored.hashes;
  } catch {
    return [];
  }
}

/** How a run says what it is doing, and roughly how long that has left. */
export type Progress = (message: string, left: string) => void;

/**
 * How long the server leaves between two requests to one host. The pacing is
 * the server's, and this is only what the estimate is made of.
 */
const HOST_GAP_MS = 5000;

/** Whether the run in progress has been asked to stop. */
let stopped = false;

export function stopFinding(): void {
  stopped = true;
}

export function findingStopped(): boolean {
  return stopped;
}

/** The profile page, or null where it could not be read at all. */
async function loadProfile(profileId: string): Promise<Profile | null> {
  try {
    const page = await fetchPage(profileUrl(profileId));
    // A profile that has been deleted or made private redirects away, and the
    // page the redirect reaches has somebody else's markup or nobody's.
    if (page.url !== profileUrl(profileId)) {
      return null;
    }
    return readProfile(new TextDecoder().decode(page.bytes));
  } catch {
    return null;
  }
}

/** What one person's lookup found, as the row will carry it. */
async function lookUp(line: ImportLine): Promise<Partial<ImportRow>> {
  const profile =
    line.profileId === undefined ? null : await loadProfile(line.profileId);
  const linkedin = profile?.linkedin ?? "";
  if (profile !== null && profile.avatar !== "") {
    const photos = await keepPhoto(avatarUrl(profile.avatar));
    if (photos.length > 0) {
      return { photos, linkedin, found: "photo from their profile" };
    }
  }
  if (line.email !== undefined) {
    const photos = await keepPhoto(await gravatarUrl(line.email));
    if (photos.length > 0) {
      return { photos, linkedin, found: "photo from Gravatar" };
    }
  }
  return { linkedin, found: "no photo found" };
}

/**
 * Look for a face for everyone still switched on, and hand each row back as it
 * lands rather than at the end: the run is one request a person against a
 * server that paces itself, so a hundred people is several minutes and a
 * screen that shows nothing for them has failed.
 *
 * A row is settled once and never revisited, so a picture pasted onto a row
 * the run has already passed is safe from it.
 */
export async function findPhotos(
  rows: ImportRow[],
  report: Progress,
  settle: (index: number, row: ImportRow) => void,
): Promise<void> {
  stopped = false;
  const wanted = rows.filter((row) => row.chosen).length;
  let done = 0;
  try {
    for (const [index, row] of rows.entries()) {
      if (findingStopped()) {
        break;
      }
      if (!row.chosen) {
        continue;
      }
      report(
        `Looking for ${row.line.name} — ${String(done + 1)} of ${String(wanted)}`,
        timeLeft((wanted - done) * HOST_GAP_MS),
      );
      done += 1;
      settle(index, { ...row, ...(await lookUp(row.line)) });
    }
  } finally {
    report("", "");
  }
}

// ---------------------------------------------------------------- adding

/** Turn one row's switch on or off. */
export function toggleRow(rows: ImportRow[], index: number): ImportRow[] {
  return rows.map((row, at) =>
    at === index ? { ...row, chosen: !row.chosen } : row,
  );
}

/** Put the photos a row was given by hand, or had taken off it, on that row. */
export function setRowPhotos(
  rows: ImportRow[],
  index: number,
  photos: string[],
): ImportRow[] {
  return rows.map((row, at) => (at === index ? { ...row, photos } : row));
}

/** Put what has been typed about somebody on their row. */
export function setRowNote(
  rows: ImportRow[],
  index: number,
  note: string,
): ImportRow[] {
  return rows.map((row, at) => (at === index ? { ...row, note } : row));
}

/**
 * Add photos to a row, keeping what it already carries. A lookup lands while
 * the screen is being worked on, so a picture pasted onto a row a moment
 * earlier is not something a found one may replace.
 */
export function addRowPhotos(
  rows: ImportRow[],
  index: number,
  photos: string[],
): ImportRow[] {
  return rows.map((row, at) =>
    at === index ? { ...row, photos: [...row.photos, ...photos] } : row,
  );
}

/** Consumer mail hosts, whose domain says nothing about where somebody works. */
const MAIL_HOSTS = [
  "aol.com",
  "btinternet.com",
  "gmail.com",
  "googlemail.com",
  "gmx.com",
  "hotmail.co.uk",
  "hotmail.com",
  "icloud.com",
  "live.com",
  "mac.com",
  "mail.com",
  "me.com",
  "msn.com",
  "outlook.com",
  "proton.me",
  "protonmail.com",
  "sky.com",
  "yahoo.co.uk",
  "yahoo.com",
];

/** The company an address is at, where its domain names one. */
function employer(email: string | undefined): string {
  const domain = email?.split("@")[1]?.toLowerCase() ?? "";
  return MAIL_HOSTS.includes(domain) ? "" : domain;
}

/**
 * Where to go looking for a face by hand: the LinkedIn profile the lookup
 * found, or an image search for the name and wherever they work.
 *
 * LinkedIn is the first place a person looks and the worst place to automate.
 * It answers a fetch with a 999 and serves a 200px photograph to the request
 * that does work. The first line of its robots.txt forbids the lot. So the
 * row offers the tab to open, and the picture comes back by paste.
 */
export function findLink(row: ImportRow): { href: string; label: string } {
  if (row.linkedin !== "") {
    return { href: row.linkedin, label: "LinkedIn" };
  }
  const query = [row.line.name, employer(row.line.email)]
    .filter((part) => part !== "")
    .join(" ");
  return {
    href: `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`,
    label: "Image search",
  };
}

/**
 * Add everyone still switched on, under one tag and at one moment. Reading the
 * clock per person instead would split one import across whichever millisecond
 * boundaries the loop happened to cross, and "newest first" would then order
 * the same import differently on a slower machine.
 *
 * @returns how many people were added.
 */
export function addChosen(
  store: PeopleStore,
  rows: ImportRow[],
  tagId: string | null,
): number {
  const now = Date.now();
  const chosen = rows.filter((row) => row.chosen);
  for (const row of chosen) {
    const input: PersonInput = {
      name: row.line.name,
      tagIds: tagId === null ? [] : [tagId],
      photos: row.photos,
    };
    // Built up rather than passed with an empty string: a person with nothing
    // written about them has no note, which is what the rest of the app reads
    // a missing field as.
    if (row.note.trim() !== "") {
      input.note = row.note.trim();
    }
    addPerson(store, input, now);
  }
  return chosen.length;
}
